import React, {
    Fragment,
    useEffect,
    useState,
    useContext,
    useCallback
} from 'react';
import '../Components/modal.css';
import axios from 'axios';
import { BASE_URL_API } from '../Constants';
import { useAuth } from '../hooks/AuthProvider';
import Select from 'react-select';
import Alert from '../Components/Alert';

import { LanguageContext } from '../context/LanguageContext';
import assetTranslations from '../languages/asset.json';
import { getErrorMessage } from '../utils/errorUtils';
import { AddNewIcon } from '../svg/UtilsIcons';
import {StepCircle} from '../svg/AssetCardIcon';
import { buildDeviceDropdownOption } from '../utils/dropdownLabels';



export const AddAssetCard = ({ refresh, setState }) => {
    const { org, permissions } = useAuth();
    const { lang } = useContext(LanguageContext);

    const canCreateAssets = permissions['create_assets'];

    const tRoot = assetTranslations[lang] || assetTranslations.en;
    const t = tRoot.addAsset;
    const isRtl = ['ar', 'ur'].includes(lang);

    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        device_id: null,
        asset_name: '',
        asset_code: '',
        category_subcategory: '',
        brand: '',
        model_number: '',
        serial_number: '',
        type: '',
        manufacturer: '',
        manufacture_date: '',
        location: '',
        operational: {
            capacity_rating: '',
            power: '',
            voltage: '',
            current: '',
            connection_type: '',
            efficiency_percent: '',
            running_hours_counter: ''
        },
        maintenance: {
            installation_date: '',
            warranty_expiry_date: '',
            condition_status: '',
            technical_evaluation: '',
            criticality_level: '',
            estimated_life_cycle_years: '' || null,
            last_maintenance_date: '',
            next_maintenance_due: '',
            o_and_m_vendor: ''
        },
        financial: {
            supplier_vendor: '',
            department_owner: '',
            purchase_cost: '' || null,
            annual_om_cost: '' || null
        },
        iot: {
            monitored_parameters: '',
            alert_thresholds: '',
            integration_protocol: ''
        }
    });

    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);

    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right'
    });

    const showAlert = useCallback(
        (type, message) => {
            setAlertState({
                open: true,
                type,
                message,
                position: isRtl ? 'left' : 'right'
            });
        },
        [isRtl]
    );

    const getFieldLabel = useCallback(
        (fieldName, section = null) => {
            return (
                t.validation.labels[fieldName] ||
                fieldName
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, char => char.toUpperCase())
            );
        },
        [t.validation.labels]
    );

    useEffect(() => {
        if (!canCreateAssets || !org?.id) {
            return;
        }

        const fetchDevices = async () => {
            try {
                const res = await axios.get(
                    `${BASE_URL_API}/device/filtered/${org.id}`
                );
                if (res.data && Array.isArray(res.data.data)) {
                    setDevices(res.data.data);
                } else {
                    throw new Error(t.invalidDevicesFormat);
                }
            } catch (err) {
                console.error('Failed to fetch devices:', err);
                const errorMessage = getErrorMessage(err);
                showAlert('error', `${t.loadDevicesError}: ${errorMessage}`);
                setDevices([]);
            }
        };

        fetchDevices();
    }, [canCreateAssets, org?.id, showAlert, t.invalidDevicesFormat, t.loadDevicesError]);

    const handleChange = (e, section = null) => {
        const { name, value, type } = e.target;

        let processedValue = value;

        if (type === 'number') {
            const cleanedValue = value.trim();
            const numValue = cleanedValue === '' ? null : Number(cleanedValue);

            if (numValue !== null && isNaN(numValue)) {
                showAlert(
                    'error',
                    `${t.validation.invalidNumber} for ${getFieldLabel(
                        name,
                        section
                    )}`
                );
                processedValue = '';
            } else if (numValue !== null && numValue < 0) {
                showAlert(
                    'error',
                    `${t.validation.noNegativeValue} for ${getFieldLabel(
                        name,
                        section
                    )}`
                );
                processedValue = '';
            } else {
                processedValue = numValue;
            }
        } else if (type === 'date') {
            processedValue = value === '' ? null : value;
        }

        if (section) {
            setFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [name]: processedValue
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: processedValue }));
        }
    };

    const handleDeviceSelect = option => {
        setSelectedDevice(option);
        setFormData(prev => ({
            ...prev,
            device_id: option ? option.value : null
        }));
    };

    const validateStep = () => {
        switch (currentStep) {
            case 1:
                if (!formData.device_id) {
                    showAlert('error', t.validation.deviceNameRequired);
                    return false;
                }
                if (!formData.asset_name || formData.asset_name.trim() === '') {
                    showAlert('error', t.validation.assetNameRequired);
                    return false;
                }
                break;
            case 2:
                break;
            default:
                break;
        }
        return true;
    };

    const handleNext = () => {
        if (validateStep()) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        setCurrentStep(prev => prev - 1);
    };

    const createAsset = async () => {
        if (!validateStep()) return;

        const payload = {
            organization_id: org.id,
            device_id: formData.device_id,

            asset_name: formData.asset_name,
            asset_code: formData.asset_code,
            category_subcategory: formData.category_subcategory,
            brand: formData.brand,
            model_number: formData.model_number,
            serial_number: formData.serial_number,
            type: formData.type,
            manufacturer: formData.manufacturer,
            manufacture_date: formData.manufacture_date,
            location: formData.location,

            operational: { ...formData.operational },
            maintenance: { ...formData.maintenance },
            financial: { ...formData.financial },
            iot: { ...formData.iot }
        };

        Object.keys(payload).forEach(key => {
            if (typeof payload[key] === 'object' && payload[key] !== null) {
                Object.keys(payload[key]).forEach(subKey => {
                    if (
                        payload[key][subKey] === '' ||
                        payload[key][subKey] === null
                    ) {
                        delete payload[key][subKey];
                    }
                });
                if (Object.keys(payload[key]).length === 0) {
                    delete payload[key];
                }
            } else if (payload[key] === '' || payload[key] === null) {
                if (key !== 'device_id' && key !== 'organization_id') {
                    delete payload[key];
                }
            }
        });

        try {
            const res = await axios.post(`${BASE_URL_API}/asset-card`, payload, {
                headers: { 'Content-Type': 'application/json' }
            });

            showAlert(
                'success',
                res.data.message || t.createSuccess
            );
            refresh();

            setTimeout(() => {
                setState(false);
            }, 2000);
        } catch (err) {
            let errorMessage = getErrorMessage(err);
            if (
                err.response?.data?.errors &&
                Array.isArray(err.response.data.errors)
            ) {
                errorMessage = err.response.data.errors
                    .map(e => e.msg)
                    .join(' ');
            }
            showAlert('error', errorMessage);
        }
    };

    if (!canCreateAssets) {
        return (
            <div
                id="modal"
                style={{ direction: isRtl ? 'rtl' : 'ltr' }}
            >
                <div
                    id="popup"
                    className="dashboard-modal asset-modal"
                    style={{
                        animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                        fontSize:
                            'calc(var(--body-font-size) * var(--font-size-multiplier))'
                    }}
                >
                    <div className="dashboard-header">
                        <div className="dashboard-header-icon">
                            <AddNewIcon />
                        </div>
                        <div className="dashboard-header-text">
                            <h2>
                                {t.permissionDenied.title}
                            </h2>
                            <p>
                                {t.permissionDenied.message}
                            </p>
                        </div>
                    </div>

                    <div className="modal-divider" />

                    <div className="modal-content dashboard-content" />

                    <div className="modal-footer dashboard-footer">
                        <button
                            className="btn btn-cancel"
                            onClick={() => setState(false)}
                        >
                            {t.buttonCancel || 'Close'}
                        </button>
                        <div />
                    </div>
                </div>
            </div>
        );
    }

    const deviceOptions = devices.map((device) => buildDeviceDropdownOption(device));

    const currentSelectedDeviceOption = selectedDevice
        ? deviceOptions.find(option => option.value === selectedDevice.value)
        : null;

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'var(--rs-control-bg)',
    borderColor: state.isFocused
      ? 'var(--rs-control-border-focus)'
      : 'var(--rs-control-border)',
    boxShadow: state.isFocused ? 'var(--rs-control-focus-shadow)' : 'none',
    '&:hover': {
      borderColor: 'var(--rs-control-border-hover)',
    },
    color: 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
    minHeight: '44px',
    borderRadius: 8,
    cursor: 'pointer',
  }),

  singleValue: (provided) => ({
    ...provided,
    color: 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
  }),

  input: (provided) => ({
    ...provided,
    color: 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
  }),

  placeholder: (provided) => ({
    ...provided,
    color: 'var(--rs-placeholder)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
  }),

  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--rs-menu-bg)',
    border: '1px solid var(--rs-menu-border)',
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: 9999,
  }),

  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),

  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--rs-option-selected-bg)'
      : state.isFocused
      ? 'var(--rs-option-hover-bg)'
      : 'var(--rs-option-bg)',
    color: state.isSelected ? 'var(--rs-option-selected-text)' : 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
    '&:active': {
      backgroundColor: 'var(--rs-option-active-bg)',
    },
    cursor: 'pointer',
  }),

  dropdownIndicator: (provided) => ({
    ...provided,
    color: 'var(--rs-indicator)',
    paddingRight: 12,
    '&:hover': {
      color: 'var(--rs-indicator-hover)',
    },
  }),

  clearIndicator: (provided) => ({
    ...provided,
    color: 'var(--rs-indicator)',
    '&:hover': {
      color: 'var(--rs-indicator-hover)',
    },
  }),

  indicatorSeparator: () => ({
    display: 'none',
  }),
};



    const stepsMeta = [
        {
            id: 1,
            key: 'step1',
            label: t.step1?.shortLabel || 'Identification & Classification'
        },
        {
            id: 2,
            key: 'step2',
            label: t.step2?.shortLabel || 'Operational Data'
        },
        {
            id: 3,
            key: 'step3',
            label: t.step3?.shortLabel || 'Maintenance & Lifecycle'
        },
        {
            id: 4,
            key: 'step4',
            label: t.step4?.shortLabel || 'Financial & Ownership'
        },
        {
            id: 5,
            key: 'step5',
            label: t.step5?.shortLabel || 'IoT & Monitoring'
        }
    ];

    const currentStepConfig = t[`step${currentStep}`] || {};


    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <Fragment>
                        <p>{t.step1.description}</p>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step1.labelDevice}
                            </label>
                            <Select
                                id="device_id_select"
                                options={deviceOptions}
                                value={currentSelectedDeviceOption}
                                onChange={handleDeviceSelect}
                                placeholder={t.step1.placeholderSelectDevice}
                                styles={customStyles}
                                menuShouldScrollIntoView={false}
                                menuPosition="fixed"
                                menuPortalTarget={
                                    typeof document !== 'undefined'
                                        ? document.body
                                        : null
                                }
                                isClearable
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step1.labelAssetName}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={t.step1.placeholderAssetName}
                                name="asset_name"
                                value={formData.asset_name}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step1.labelAssetCode}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={t.step1.placeholderAssetCode}
                                name="asset_code"
                                value={formData.asset_code}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step1.labelCategorySubcategory}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={
                                    t.step1.placeholderCategorySubcategory
                                }
                                name="category_subcategory"
                                value={formData.category_subcategory}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step1.labelBrand}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={t.step1.placeholderBrand}
                                name="brand"
                                value={formData.brand}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step1.labelModelNumber}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={t.step1.placeholderModelNumber}
                                name="model_number"
                                value={formData.model_number}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step1.labelSerialNumber}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={t.step1.placeholderSerialNumber}
                                name="serial_number"
                                value={formData.serial_number}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step1.labelType}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={t.step1.placeholderType}
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step1.labelManufacturer}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={t.step1.placeholderManufacturer}
                                name="manufacturer"
                                value={formData.manufacturer}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step1.labelManufactureDate}
                            </label>
                            <input
                                type="date"
                                className="add-column"
                                name="manufacture_date"
                                value={formData.manufacture_date || ''}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step1.labelLocation}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={t.step1.placeholderLocation}
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                            />
                        </div>
                    </Fragment>
                );
            case 2:
                return (
                    <Fragment>
                        <p>{t.step2.description}</p>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step2.labelCapacityRating}
                            </label>
                            <input
                                type="number"
                                className="add-column"
                                placeholder={t.step2.placeholderCapacityRating}
                                name="capacity_rating"
                                value={formData.operational.capacity_rating || ''}
                                onChange={e => handleChange(e, 'operational')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">{t.step2.labelPower}</label>
                            <input
                                type="number"
                                className="add-column"
                                placeholder={t.step2.placeholderPower}
                                name="power"
                                value={formData.operational.power || ''}
                                onChange={e => handleChange(e, 'operational')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step2.labelVoltage}
                            </label>
                            <input
                                type="number"
                                className="add-column"
                                placeholder={t.step2.placeholderVoltage}
                                name="voltage"
                                value={formData.operational.voltage || ''}
                                onChange={e => handleChange(e, 'operational')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step2.labelCurrent}
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                className="add-column"
                                placeholder={t.step2.placeholderCurrent}
                                name="current"
                                value={formData.operational.current || ''}
                                onChange={e => handleChange(e, 'operational')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step2.labelConnectionType}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={t.step2.placeholderConnectionType}
                                name="connection_type"
                                value={formData.operational.connection_type}
                                onChange={e => handleChange(e, 'operational')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step2.labelEfficiencyPercent}
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                className="add-column"
                                placeholder={t.step2.placeholderEfficiencyPercent}
                                name="efficiency_percent"
                                value={
                                    formData.operational.efficiency_percent || ''
                                }
                                onChange={e => handleChange(e, 'operational')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step2.labelRunningHoursCounter}
                            </label>
                            <input
                                type="number"
                                className="add-column"
                                placeholder={
                                    t.step2.placeholderRunningHoursCounter
                                }
                                name="running_hours_counter"
                                value={
                                    formData.operational.running_hours_counter ||
                                    ''
                                }
                                onChange={e => handleChange(e, 'operational')}
                            />
                        </div>
                    </Fragment>
                );
            case 3:
                return (
                    <Fragment>
                        <p>{t.step3.description}</p>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step3.labelInstallationDate}
                            </label>
                            <input
                                type="date"
                                className="add-column"
                                name="installation_date"
                                value={
                                    formData.maintenance.installation_date || ''
                                }
                                onChange={e => handleChange(e, 'maintenance')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step3.labelWarrantyExpiryDate}
                            </label>
                            <input
                                type="date"
                                className="add-column"
                                name="warranty_expiry_date"
                                value={
                                    formData.maintenance.warranty_expiry_date ||
                                    ''
                                }
                                onChange={e => handleChange(e, 'maintenance')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step3.labelConditionStatus}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={t.step3.placeholderConditionStatus}
                                name="condition_status"
                                value={formData.maintenance.condition_status}
                                onChange={e => handleChange(e, 'maintenance')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step3.labelTechnicalEvaluation}
                            </label>
                            <textarea
                                className="add-column"
                                placeholder={
                                    t.step3.placeholderTechnicalEvaluation
                                }
                                rows="3"
                                name="technical_evaluation"
                                value={formData.maintenance.technical_evaluation}
                                onChange={e => handleChange(e, 'maintenance')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step3.labelCriticalityLevel}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={t.step3.placeholderCriticalityLevel}
                                name="criticality_level"
                                value={formData.maintenance.criticality_level}
                                onChange={e => handleChange(e, 'maintenance')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step3.labelEstimatedLifeCycleYears}
                            </label>
                            <input
                                type="number"
                                className="add-column"
                                placeholder={
                                    t.step3.placeholderEstimatedLifeCycleYears
                                }
                                name="estimated_life_cycle_years"
                                value={
                                    formData.maintenance
                                        .estimated_life_cycle_years || ''
                                }
                                onChange={e => handleChange(e, 'maintenance')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step3.labelLastMaintenanceDate}
                            </label>
                            <input
                                type="date"
                                className="add-column"
                                name="last_maintenance_date"
                                value={
                                    formData.maintenance.last_maintenance_date ||
                                    ''
                                }
                                onChange={e => handleChange(e, 'maintenance')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step3.labelNextMaintenanceDue}
                            </label>
                            <input
                                type="date"
                                className="add-column"
                                name="next_maintenance_due"
                                value={
                                    formData.maintenance.next_maintenance_due ||
                                    ''
                                }
                                onChange={e => handleChange(e, 'maintenance')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step3.labelOAndMVendor}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={t.step3.placeholderOAndMVendor}
                                name="o_and_m_vendor"
                                value={formData.maintenance.o_and_m_vendor}
                                onChange={e => handleChange(e, 'maintenance')}
                            />
                        </div>
                    </Fragment>
                );
            case 4:
                return (
                    <Fragment>
                        <p>{t.step4.description}</p>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step4.labelSupplierVendor}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={t.step4.placeholderSupplierVendor}
                                name="supplier_vendor"
                                value={formData.financial.supplier_vendor}
                                onChange={e => handleChange(e, 'financial')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step4.labelDepartmentOwner}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={
                                    t.step4.placeholderDepartmentOwner
                                }
                                name="department_owner"
                                value={formData.financial.department_owner}
                                onChange={e => handleChange(e, 'financial')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step4.labelPurchaseCost}
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                className="add-column"
                                placeholder={t.step4.placeholderPurchaseCost}
                                name="purchase_cost"
                                value={formData.financial.purchase_cost || ''}
                                onChange={e => handleChange(e, 'financial')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step4.labelAnnualOmCost}
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                className="add-column"
                                placeholder={t.step4.placeholderAnnualOmCost}
                                name="annual_om_cost"
                                value={formData.financial.annual_om_cost || ''}
                                onChange={e => handleChange(e, 'financial')}
                            />
                        </div>
                    </Fragment>
                );
            case 5:
                return (
                    <Fragment>
                        <p>{t.step5.description}</p>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step5.labelMonitoredParameters}
                            </label>
                            <textarea
                                className="add-column"
                                placeholder={
                                    t.step5.placeholderMonitoredParameters
                                }
                                rows="3"
                                name="monitored_parameters"
                                value={formData.iot.monitored_parameters}
                                onChange={e => handleChange(e, 'iot')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step5.labelAlertThresholds}
                            </label>
                            <textarea
                                className="add-column"
                                placeholder={t.step5.placeholderAlertThresholds}
                                rows="3"
                                name="alert_thresholds"
                                value={formData.iot.alert_thresholds}
                                onChange={e => handleChange(e, 'iot')}
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-lbl">
                                {t.step5.labelIntegrationProtocol}
                            </label>
                            <input
                                type="text"
                                className="add-column"
                                placeholder={
                                    t.step5.placeholderIntegrationProtocol
                                }
                                name="integration_protocol"
                                value={formData.iot.integration_protocol}
                                onChange={e => handleChange(e, 'iot')}
                            />
                        </div>
                    </Fragment>
                );
            default:
                return null;
        }
    };

    return (
        <div id="modal" style={{ direction: isRtl ? 'rtl' : 'ltr' }}>
            <Alert
                open={alertState.open}
                close={() =>
                    setAlertState({ ...alertState, open: false })
                }
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />

            <div
                id="popup"
                className="dashboard-modal asset-modal tall"
                style={{
                    animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                    fontSize:
                        'calc(var(--body-font-size) * var(--font-size-multiplier))'
                }}
            >
                <div className="dashboard-header">
                    <div className="dashboard-header-icon">
                        <AddNewIcon />
                    </div>
                    <div className="dashboard-header-text">
                        <h2>{t.title}</h2>
                        <p>
                            {currentStepConfig.header ||
                                currentStepConfig.description ||
                                ''}
                        </p>
                    </div>
                </div>

                <div className="modal-divider" />

                <div className="asset-stepper">
                    <div className="asset-stepper-track">
                        {stepsMeta.map(step => {
                            let status = 'upcoming';
                            if (currentStep > step.id) status = 'done';
                            else if (currentStep === step.id) status = 'current';

                            return (
                                <div key={step.id} className="asset-step">
                                    <StepCircle status={status} />
                                </div>
                            );
                        })}
                    </div>
                    <div className="asset-stepper-labels">
                        {stepsMeta.map(step => (
                            <div key={step.id} className="asset-step-label">
                                {step.label}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="modal-content dashboard-content">
                    {renderStep()}
                </div>

                <div className="modal-footer dashboard-footer">
                    <button
                        className="btn btn-cancel"
                        onClick={() => setState(false)}
                    >
                        {t.buttonCancel}
                    </button>

                    <div className="modal-footer-right-buttons">
                        {currentStep > 1 && (
                            <button
                                className="btn btn-cancel btn-back"
                                onClick={handleBack}
                            >
                                {t.buttonBack}
                            </button>
                        )}

                        {currentStep < 5 && (
                            <button
                                className="btn btn-apply dashboard-primary-btn"
                                onClick={handleNext}
                            >
                                {t.buttonNext}
                            </button>
                        )}

                        {currentStep === 5 && (
                            <button
                                className="btn btn-apply dashboard-primary-btn"
                                onClick={createAsset}
                            >
                                {t.buttonCreate}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
